// React
import React from 'react';
import { v4 } from 'uuid';

// Mantine
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table';

// Refine
import { useDocumentTitle } from '@refinedev/react-router-v6';

//Components
import { CreateRowButton } from 'components/common/CreateRowButton';
import { SiteModal } from './components/SiteModal';
import { EditRowButton } from 'components/common/EditRowButton';
import { TableAlertBanner } from 'components/common/TableAlertBanner';
import { TableProgressBar } from 'components/common/TableProgressBar';

// Interface
import { Organization, Payor, Site } from 'interfaces';

// Columns
import { SiteColumns } from 'columns/Site';

// Auth
import { useIsAdmin, useCanModify } from 'hooks';

//API
import { useCreateSite, useUpdateSite } from 'api/resource';
import {
  CreateMutationDataParams,
  UpdateMutationDataParams,
  createMutationData,
  updateMutationData,
} from 'api/utils/MutationDataBuilder';
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { validateSiteContactValues } from '../../../validation/site';

/**
 * - Expand design to not look so plain and similar to users table
 * - Maybe add row expansion and show associated users in each site
 */

interface SitesManagementProps {
  sites: Site[];
  payors: Payor[];
  organizations: Organization[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: string;
}

export const SitesManagement: React.FC<SitesManagementProps> = ({
  sites,
  payors,
  organizations,
  isLoading,
  isError,
  isFetching,
  errorMessage,
}) => {
  const title = 'Site Management';
  useDocumentTitle('Sites | QBilling');
  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('sites');
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();

  const initialVisibleColumns = [
    'name',
    'address',
    'region',
    'email',
    'phone',
    'postalCode',
    'fax',
    'status',
    'invoicee',
    'overhead',
    'invoiceRate',
    'description',
    'proFees',
    'techFees',
    'timezone',
    'defaultPayorID',
  ];

  const handleCreateSite = async ({
    table,
    values,
  }: CreateMutationDataParams<Site>) => {
    const { normalizedPostalCode, normalizedFax, isValid } =
      validateSiteContactValues({
        postalCode: values.postalCode,
        fax: values.fax,
      });

    if (!isValid) {
      return;
    }

    const newSite: Site = {
      ...values,
      postalCode: normalizedPostalCode,
      fax: normalizedFax,
    };
    return createMutationData<Site>({
      table,
      values: {
        ...newSite,
        id: values.id || v4(),
      },
      mutation: createSite,
    });
  };

  const handleUpdateSite = async ({
    table,
    values,
    row,
  }: UpdateMutationDataParams<Site>): Promise<void> => {
    updateMutationData<Site>({
      table,
      values,
      row,
      mutation: updateSite,
    });
  };

  const getDefaultColumnVisibility = (
    columns: MRT_ColumnDef<Site>[],
    visibleColumns: string[]
  ) => {
    return columns.reduce((visibility: { [key: string]: boolean }, column) => {
      visibility[column.accessorKey as string] = visibleColumns.includes(
        column.accessorKey as string
      );
      return visibility;
    }, {});
  };

  const data = sites ?? [];
  const columns: MRT_ColumnDef<Site>[] = SiteColumns(
    organizations,
    isAdmin,
    payors
  );

  const initialColumns = getDefaultColumnVisibility(
    columns,
    initialVisibleColumns
  );

  const [visibleColumns] = React.useState(initialColumns);

  const tableOptions: MRT_TableOptions<Site> = {
    data,
    columns,
    state: {
      isLoading: isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      showLoadingOverlay: isFetching || isLoading,
      columnVisibility: visibleColumns,
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    mantineCreateRowModalProps: {
      withCloseButton: true,
      title: 'Create Site',
      size: '70%',
      centered: true,
      withOverlay: true,
      overlayProps: {
        backgroundOpacity: 0.5,
        blur: 5,
      },
      styles: {
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
        },
        title: {
          fontSize: '1.875rem',
          textAlign: 'center',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
        },
      },
    },
    mantineEditRowModalProps: {
      withCloseButton: true,
      title: 'Edit Site',
      size: '70%',
      centered: true,
      withOverlay: true,
      overlayProps: {
        backgroundOpacity: 0.5,
        blur: 5,
      },
      styles: {
        header: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          alignContent: 'center',
        },
        title: {
          fontSize: '1.875rem',
          textAlign: 'center',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
        },
      },
    },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    enableFullScreenToggle: false,
    enableEditing: true,
    onCreatingRowSave: handleCreateSite,
    onEditingRowSave: handleUpdateSite,
    renderTopToolbarCustomActions: ({ table }) => {
      return CreateRowButton<Site>({
        table,
        type: 'site',
        disabled: isFetching || isLoading || !canModify,
        isLoading: isLoading,
      });
    },
    renderCreateRowModalContent: ({ table, row, internalEditComponents }) => {
      return SiteModal({
        table,
        row,
        internalEditComponents,
        type: 'create',
      });
    },
    renderEditRowModalContent: ({ table, row, internalEditComponents }) => {
      return SiteModal({
        table,
        row,
        internalEditComponents,
        type: 'edit',
      });
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        Cell: ({
          table,
          row,
        }: {
          table: MRT_TableInstance<Site>;
          row: MRT_Row<Site>;
        }) => (
          <EditRowButton
            table={table}
            row={row}
            type="site"
            disabled={!canModify}
          />
        ),
      },
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      dataTestID="siteManagement"
      tableOptions={tableOptions}
      showAlertBanner={false}
    />
  );
};
